import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, UrlSegment } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleEnum } from 'app/modules/shared/enums/roleEnum';
import { User } from 'app/core/user/user.types';
import { AuthUtils } from '../auth.utils';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { of, switchMap } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { UserUtil } from 'app/modules/shared/utils/user.util';

/**
 * Auth Check all these points:
 * 1. is user authenticated
 * 2. is user role able him to get the page
 * @param route
 * @param state 
 * @returns 
 */
export const TeamAuthGuard: CanActivateFn | CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const userService = inject(UserService);

    return authService.check().pipe(
        switchMap((authenticated: boolean) => {
            // 1. Check if the user is authenticated
            if (!authenticated) {

                // Clear the user session
                authService.signOut();

                // Redirect to the sign-in page with a redirectUrl param
                const urlTree = router.parseUrl(PathEnum.APP_TEAM + '/' + PathEnum.AUTH_SIGN_IN + `?redirectURL=${buildUrlPath(state)}`);
                return of(urlTree);
            } else {
                // 3. Check if the user has the mandatory role
                const userObs = userService.user$;
                return userObs.pipe(
                    switchMap(user => {
                        if (!user) {
                            console.error("Erreur d'initialisation de l'application");
                            return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_500]));
                        }
                        const allowedRoles = route.data['allowedRoles'] as RoleEnum[];
                        const res = checkUserRoles(allowedRoles);
                        if (!res) {
                            console.error("User don't have the roles for this page");
                            return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_403]));
                        }
                        // Continuer avec d'autres vérifications si nécessaire
                        return of(res);
                    })
                );
            }
        })
    );
};

function buildUrlPath(state: UrlSegment[] | RouterStateSnapshot) {
    let path = '';
    if (Array.isArray(state)) {
        let i: number = 0;
        while (i < state.length) {
            path += state[i].path + '/';
            i++;
        }
        path = path.substring(0, path.length - 1);
    } else {
        path = state.url;
    }

    return path;
}

function checkUserRoles(allowedRoles: string[]): boolean {
    const user: User = AuthUtils.getUserToken(); // Assuming this method synchronously returns the user token

    // Check if all users can access
    if (!allowedRoles || allowedRoles.length === 0 || allowedRoles.includes(RoleEnum.ALL)) {
        return true;
    }

    // Check if all team users can access
    if (!allowedRoles || allowedRoles.length === 0 || (UserUtil.isTeamUser(user) && allowedRoles.includes(RoleEnum.ATR))) {
        return true;
    }

    const userHasRequiredRole = user.roles.some(role => allowedRoles.includes(role as RoleEnum));

    if (userHasRequiredRole) {
        return true;
    } else {
        return false;
    }
}

